import { useCallback, useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { useTitle } from 'react-use';

import { ReadOnlyBanner } from '@/components/atoms/ReadOnlyBanner/ReadOnlyBanner';
import Value from '@/components/organisms/Entities/Value/Value';
import SubHeaderEntity from '@/components/organisms/SubHeader/SubHeaderEntity';
import SubNavEntity from '@/components/organisms/SubNav/SubNavEntity';
import { makeItemsName } from '@/components/organisms/SubNav/utils';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { Skeleton } from '@/components/ui/skeleton';
import useBrains from '@/hooks/useBrains';
import useDialogNodes from '@/hooks/useDialogNodes';
import useEntities from '@/hooks/useEntities';
import useValues from '@/hooks/useValues';
import { cn } from '@/lib/utils';
import { popModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { setEntityName } from '@/redux/session/actions';
import { selectAccountSlug, selectBrainId } from '@/redux/session/selectors';
import { clearEntityValues, setEntity } from '@/redux/values/actions';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

import styles from './Entity.module.scss';

const Entity = () => {
  const { t } = useTranslation();
  const { brainId, entityName } = useParams();
  const sessionBrainId = useSelector(selectBrainId);
  const { brain } = useBrains(brainId);
  const { entity, entities } = useEntities(brainId, entityName);

  useDialogNodes({ type: 'entity' });

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );

  const slug = useSelector(selectAccountSlug);
  const { values, findConnectedDialogsForValues, isDraft } = useValues(brainId, entityName);
  const draftInitialized = useRef(false);

  useTitle(t('pages.entity', { 0: brain?.name ?? '-' }));
  useSegments(PageName.ENTITY);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleDialogNameClick = useCallback(
    (dialog_id) => {
      const path = `/${slug}/ai-agents/${brainId}/workflows/dialogs/${dialog_id}`;

      dispatch(popModal());
      navigate(path);
    },
    [brainId, dispatch, navigate, slug]
  );

  useEffect(() => {
    if (sessionBrainId && !isDraft) {
      dispatch(setEntityName(entityName));
    }
  }, [dispatch, entityName, isDraft, sessionBrainId]);

  useEffect(() => {
    if (entity && !isDraft) {
      dispatch(setEntity(entity));
      return () => {
        dispatch(clearEntityValues());
      };
    }
  }, [dispatch, entity, isDraft]);

  useEffect(() => {
    if (isDraft && !draftInitialized.current && entities) {
      draftInitialized.current = true;
      const newEntityName = makeItemsName(entities, 'entity', 'Entity_name');
      dispatch(setEntity({ entity: newEntityName, values: [] }));
    }
  }, [dispatch, entities, isDraft]);

  useEffect(() => {
    if (!isDraft) {
      draftInitialized.current = false;
    }
    return () => {
      dispatch(clearEntityValues());
    };
  }, [dispatch, isDraft]);

  const renderSkeletons = useCallback(
    () =>
      Array(5)
        .fill('')
        .map((_, i) => (
          <Skeleton
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            className="entity__input h-12 w-full"
          />
        )),
    []
  );

  return (
    <>
      {!canWrite && <ReadOnlyBanner />}

      <div className={cn(styles.wrapper, 'h-full', { [styles.readOnly]: !canWrite })}>
        <SubNavEntity />
        <SubHeaderEntity />
        <div className="col-start-2 row-start-2 flex h-full min-h-0 flex-col overflow-hidden *:h-full *:max-h-full *:min-h-0">
          <PageContentWrapper>
            <ul>
              {!values && renderSkeletons()}
              {values &&
                values.map(({ id }) => (
                  <Value
                    key={id}
                    id={id}
                    readOnly={!canWrite}
                    findConnectedDialogs={findConnectedDialogsForValues}
                    handleDialogNameClick={handleDialogNameClick}
                  />
                ))}
              {values && <Value id="" readOnly={!canWrite} key={values?.length} />}
            </ul>
          </PageContentWrapper>
        </div>
      </div>
    </>
  );
};

export default Entity;
